import { useState, useEffect, useCallback } from 'react';
import { performance as perfApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../theme';

// ─── Constants ────────────────────────────────────────────────────────────────

const RATING_LABELS = {
  1: 'Extremely Poor', 2: 'Very Poor', 3: 'Poor', 4: 'Below Average',
  5: 'Average', 6: 'Slightly Above Average', 7: 'Good',
  8: 'Very Good', 9: 'Excellent', 10: 'Outstanding',
};

const STATUS_COLORS = {
  draft: { bg: '#F3F4F6', text: '#6B7280' },
  active: { bg: '#DBEAFE', text: '#1D4ED8' },
  on_track: { bg: '#D1FAE5', text: '#065F46' },
  at_risk: { bg: '#FEF3C7', text: '#92400E' },
  completed: { bg: '#D1FAE5', text: '#065F46' },
  cancelled: { bg: '#FEE2E2', text: '#991B1B' },
};

const COMPETENCY_LEVELS = ['beginner', 'developing', 'proficient', 'advanced', 'expert'];
const COMPETENCY_COLORS = {
  beginner: '#EF4444', developing: '#F97316', proficient: '#F59E0B',
  advanced: '#3B82F6', expert: '#10B981',
};

const FEEDBACK_TYPES = ['feedback', 'coaching', 'journal', 'recognition'];
const FEEDBACK_ICONS = { feedback: '💬', coaching: '🎯', journal: '📓', recognition: '🏆' };

const inp = {
  width: '100%', padding: '0.6rem 0.875rem',
  border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem',
  fontSize: '0.875rem', color: theme.text, outline: 'none',
  boxSizing: 'border-box', background: '#fff',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function ratingColor(n) {
  if (!n) return theme.textMuted;
  if (n >= 9) return '#10B981';
  if (n >= 7) return '#3B82F6';
  if (n >= 5) return '#F59E0B';
  if (n >= 3) return '#F97316';
  return '#EF4444';
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Sub-Components ───────────────────────────────────────────────────────────

function StatPill({ label, value, color = theme.primary }) {
  return (
    <div style={{ background: theme.white, border: `1px solid ${theme.border}`, borderLeft: `4px solid ${color}`, borderRadius: '0.5rem', padding: '0.75rem 1.25rem', minWidth: 120 }}>
      <div style={{ fontSize: '1.4rem', fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: '0.75rem', color: theme.textMuted, marginTop: 2 }}>{label}</div>
    </div>
  );
}

function ProgressBar({ value, max = 100, color = '#3B82F6' }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div style={{ background: '#F3F4F6', borderRadius: 999, height: 8, overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 999, transition: 'width 0.4s' }} />
    </div>
  );
}

function RatingInput({ value, onChange, readOnly }) {
  return (
    <div>
      <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginTop: '0.375rem' }}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
          <button key={n} type="button"
            onClick={() => !readOnly && onChange(value === n ? null : n)}
            style={{
              width: 34, height: 34, borderRadius: '50%',
              border: `2px solid ${value === n ? ratingColor(n) : '#E5E7EB'}`,
              background: value === n ? ratingColor(n) : '#F9FAFB',
              color: value === n ? '#fff' : '#6B7280',
              fontWeight: 700, cursor: readOnly ? 'default' : 'pointer',
              fontSize: '0.8rem', transition: 'all 0.15s',
            }}
          >{n}</button>
        ))}
      </div>
      {value && (
        <div style={{ marginTop: '0.3rem', fontSize: '0.75rem', fontWeight: 600, color: ratingColor(value) }}>
          {value}/10 — {RATING_LABELS[value]}
        </div>
      )}
    </div>
  );
}

function Modal({ isOpen, onClose, title, children, width = 600 }) {
  if (!isOpen) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: '#fff', borderRadius: '1rem', width: '100%', maxWidth: width, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: theme.text }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: theme.textMuted, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '1.5rem' }}>{children}</div>
      </div>
    </div>
  );
}

function GoalStatusBadge({ status }) {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: 999, background: colors.bg, color: colors.text, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {status?.replace('_', ' ')}
    </span>
  );
}

// ─── Goal Card ────────────────────────────────────────────────────────────────

function GoalCard({ goal, onCheckin, onEdit, isOwn }) {
  const [expanded, setExpanded] = useState(false);
  const pct = goal.progress_pct ?? 0;
  const progressColor = pct >= 80 ? '#10B981' : pct >= 50 ? '#3B82F6' : pct >= 25 ? '#F59E0B' : '#EF4444';

  return (
    <div style={{ background: theme.white, border: `1px solid ${theme.border}`, borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '0.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
            <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 999, background: goal.goal_type === 'okr' ? '#EDE9FE' : '#DBEAFE', color: goal.goal_type === 'okr' ? '#7C3AED' : '#1D4ED8', textTransform: 'uppercase' }}>{goal.goal_type}</span>
            <GoalStatusBadge status={goal.status} />
            {goal.level && <span style={{ fontSize: '0.7rem', color: theme.textMuted }}>• {goal.level}</span>}
            {goal.weight && <span style={{ fontSize: '0.7rem', color: theme.textMuted }}>• {goal.weight}% weight</span>}
          </div>
          <p style={{ margin: '0 0 0.25rem', fontWeight: 700, color: theme.text, fontSize: '0.95rem' }}>{goal.title}</p>
          {goal.description && <p style={{ margin: 0, fontSize: '0.8rem', color: theme.textMuted }}>{goal.description}</p>}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: '1.3rem', fontWeight: 700, color: progressColor }}>{pct}%</div>
          <div style={{ fontSize: '0.7rem', color: theme.textMuted }}>progress</div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ marginTop: '0.75rem' }}>
        <ProgressBar value={pct} color={progressColor} />
        {goal.target_value && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem', fontSize: '0.72rem', color: theme.textMuted }}>
            <span>Current: {goal.current_value ?? 0} {goal.unit}</span>
            <span>Target: {goal.target_value} {goal.unit}</span>
          </div>
        )}
      </div>

      {/* Key results */}
      {goal.key_results?.length > 0 && (
        <div style={{ marginTop: '0.75rem' }}>
          <button onClick={() => setExpanded(!expanded)} style={{ background: 'none', border: 'none', fontSize: '0.78rem', color: theme.primary, cursor: 'pointer', padding: 0, fontWeight: 600 }}>
            {expanded ? '▲' : '▼'} {goal.key_results.length} Key Result{goal.key_results.length > 1 ? 's' : ''}
          </button>
          {expanded && (
            <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {goal.key_results.map((kr, i) => {
                const krPct = kr.target_value > 0 ? Math.min(100, Math.round((kr.current_value / kr.target_value) * 100)) : 0;
                return (
                  <div key={i} style={{ background: '#F9FAFB', borderRadius: '0.5rem', padding: '0.5rem 0.75rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                      <span style={{ fontSize: '0.8rem', color: theme.text }}>{kr.title}</span>
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: progressColor }}>{krPct}%</span>
                    </div>
                    <ProgressBar value={krPct} color={progressColor} />
                    <div style={{ fontSize: '0.7rem', color: theme.textMuted, marginTop: '0.2rem' }}>{kr.current_value ?? 0} / {kr.target_value} {kr.unit}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '0.72rem', color: theme.textMuted }}>
          {goal.due_date && <>Due: {fmtDate(goal.due_date)}</>}
          {goal.checkins_count > 0 && <> • {goal.checkins_count} check-in{goal.checkins_count > 1 ? 's' : ''}</>}
        </div>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          {isOwn && onCheckin && ['active', 'on_track', 'at_risk'].includes(goal.status) && (
            <button onClick={() => onCheckin(goal)} style={{ padding: '0.3rem 0.75rem', fontSize: '0.78rem', background: '#DBEAFE', color: '#1D4ED8', border: 'none', borderRadius: '0.4rem', cursor: 'pointer', fontWeight: 600 }}>
              Check-in
            </button>
          )}
          {isOwn && onEdit && (
            <button onClick={() => onEdit(goal)} style={{ padding: '0.3rem 0.75rem', fontSize: '0.78rem', background: '#F3F4F6', color: theme.text, border: 'none', borderRadius: '0.4rem', cursor: 'pointer' }}>
              Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── My Goals Tab ─────────────────────────────────────────────────────────────

function MyGoalsTab({ user }) {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [checkinGoal, setCheckinGoal] = useState(null);
  const [editGoal, setEditGoal] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [checkinForm, setCheckinForm] = useState({ current_value: '', notes: '', status: '' });
  const [goalForm, setGoalForm] = useState({ title: '', description: '', goal_type: 'okr', level: 'individual', cycle_type: 'quarterly', year: new Date().getFullYear(), quarter: 1, target_value: '', unit: '', weight: '', due_date: '', status: 'active' });
  const [keyResults, setKeyResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filterStatus) params.status = filterStatus;
    if (filterType) params.goal_type = filterType;
    perfApi.getMyGoals(params).then(r => { setGoals(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, [filterStatus, filterType]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setGoalForm({ title: '', description: '', goal_type: 'okr', level: 'individual', cycle_type: 'quarterly', year: new Date().getFullYear(), quarter: 1, target_value: '', unit: '', weight: '', due_date: '', status: 'active' });
    setKeyResults([]);
    setEditGoal(null);
    setShowCreate(true);
  };

  const openEdit = (g) => {
    setGoalForm({ title: g.title, description: g.description || '', goal_type: g.goal_type, level: g.level, cycle_type: g.cycle_type, year: g.year, quarter: g.quarter || 1, target_value: g.target_value || '', unit: g.unit || '', weight: g.weight || '', due_date: g.due_date || '', status: g.status });
    setKeyResults(g.key_results || []);
    setEditGoal(g);
    setShowCreate(true);
  };

  const saveGoal = async () => {
    if (!goalForm.title.trim()) { setError('Title is required'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...goalForm, target_value: goalForm.target_value ? parseFloat(goalForm.target_value) : null, weight: goalForm.weight ? parseFloat(goalForm.weight) : null, key_results: keyResults };
      if (editGoal) await perfApi.updateGoal(editGoal.id, payload);
      else await perfApi.createGoal(payload);
      setShowCreate(false);
      load();
    } catch (e) { setError(e.response?.data?.detail || 'Failed to save goal'); }
    finally { setSaving(false); }
  };

  const saveCheckin = async () => {
    if (!checkinGoal) return;
    setSaving(true);
    try {
      const payload = { current_value: checkinForm.current_value ? parseFloat(checkinForm.current_value) : null, notes: checkinForm.notes, status: checkinForm.status || null };
      await perfApi.addCheckin(checkinGoal.id, payload);
      setCheckinGoal(null);
      setCheckinForm({ current_value: '', notes: '', status: '' });
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to save check-in'); }
    finally { setSaving(false); }
  };

  const addKR = () => setKeyResults(prev => [...prev, { title: '', target_value: '', current_value: 0, unit: '' }]);
  const updateKR = (i, k, v) => setKeyResults(prev => prev.map((kr, idx) => idx === i ? { ...kr, [k]: v } : kr));
  const removeKR = (i) => setKeyResults(prev => prev.filter((_, idx) => idx !== i));

  const stats = {
    total: goals.length,
    onTrack: goals.filter(g => g.status === 'on_track').length,
    atRisk: goals.filter(g => g.status === 'at_risk').length,
    completed: goals.filter(g => g.status === 'completed').length,
    avgProgress: goals.length ? Math.round(goals.reduce((a, g) => a + (g.progress_pct || 0), 0) / goals.length) : 0,
  };

  return (
    <div>
      {/* Stats */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <StatPill label="Total Goals" value={stats.total} color="#3B82F6" />
        <StatPill label="On Track" value={stats.onTrack} color="#10B981" />
        <StatPill label="At Risk" value={stats.atRisk} color="#F59E0B" />
        <StatPill label="Completed" value={stats.completed} color="#8B5CF6" />
        <StatPill label="Avg Progress" value={`${stats.avgProgress}%`} color="#0EA5E9" />
      </div>

      {/* Filters + Create */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inp, width: 150, resize: undefined }}>
          <option value="">All Statuses</option>
          {['draft', 'active', 'on_track', 'at_risk', 'completed', 'cancelled'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ ...inp, width: 130, resize: undefined }}>
          <option value="">All Types</option>
          <option value="okr">OKR</option>
          <option value="kpi">KPI</option>
        </select>
        <button onClick={openCreate} style={{ marginLeft: 'auto', padding: '0.5rem 1.25rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem' }}>
          + New Goal
        </button>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>Loading...</div> : (
        goals.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted, background: theme.white, borderRadius: '0.75rem', border: `1px solid ${theme.border}` }}>
            No goals found. Create your first goal!
          </div>
        ) : (
          goals.map(g => <GoalCard key={g.id} goal={g} onCheckin={setCheckinGoal} onEdit={openEdit} isOwn />)
        )
      )}

      {/* Create/Edit Goal Modal */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title={editGoal ? 'Edit Goal' : 'Create Goal'} width={680}>
        {error && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '0.6rem 1rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.85rem' }}>{error}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div style={{ gridColumn: '1/-1' }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text }}>Title *</label>
            <input style={inp} value={goalForm.title} onChange={e => setGoalForm(f => ({ ...f, title: e.target.value }))} placeholder="Goal title" />
          </div>
          <div style={{ gridColumn: '1/-1' }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text }}>Description</label>
            <textarea style={{ ...inp, resize: 'vertical', minHeight: 70 }} value={goalForm.description} onChange={e => setGoalForm(f => ({ ...f, description: e.target.value }))} placeholder="What does success look like?" />
          </div>
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text }}>Goal Type</label>
            <select style={{ ...inp, resize: undefined }} value={goalForm.goal_type} onChange={e => setGoalForm(f => ({ ...f, goal_type: e.target.value }))}>
              <option value="okr">OKR</option>
              <option value="kpi">KPI</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text }}>Status</label>
            <select style={{ ...inp, resize: undefined }} value={goalForm.status} onChange={e => setGoalForm(f => ({ ...f, status: e.target.value }))}>
              {['draft', 'active', 'on_track', 'at_risk'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text }}>Cycle</label>
            <select style={{ ...inp, resize: undefined }} value={goalForm.cycle_type} onChange={e => setGoalForm(f => ({ ...f, cycle_type: e.target.value }))}>
              <option value="quarterly">Quarterly</option>
              <option value="half_yearly">Half Yearly</option>
              <option value="annual">Annual</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text }}>Year</label>
            <input type="number" style={inp} value={goalForm.year} onChange={e => setGoalForm(f => ({ ...f, year: parseInt(e.target.value) }))} />
          </div>
          {goalForm.cycle_type === 'quarterly' && (
            <div>
              <label style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text }}>Quarter</label>
              <select style={{ ...inp, resize: undefined }} value={goalForm.quarter} onChange={e => setGoalForm(f => ({ ...f, quarter: parseInt(e.target.value) }))}>
                <option value={1}>Q1</option><option value={2}>Q2</option><option value={3}>Q3</option><option value={4}>Q4</option>
              </select>
            </div>
          )}
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text }}>Target Value</label>
            <input type="number" style={inp} value={goalForm.target_value} onChange={e => setGoalForm(f => ({ ...f, target_value: e.target.value }))} placeholder="e.g. 100" />
          </div>
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text }}>Unit</label>
            <input style={inp} value={goalForm.unit} onChange={e => setGoalForm(f => ({ ...f, unit: e.target.value }))} placeholder="e.g. %, deals, hrs" />
          </div>
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text }}>Weight (%)</label>
            <input type="number" style={inp} value={goalForm.weight} onChange={e => setGoalForm(f => ({ ...f, weight: e.target.value }))} placeholder="e.g. 30" />
          </div>
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text }}>Due Date</label>
            <input type="date" style={inp} value={goalForm.due_date} onChange={e => setGoalForm(f => ({ ...f, due_date: e.target.value }))} />
          </div>
        </div>

        {/* Key Results (OKR only) */}
        {goalForm.goal_type === 'okr' && (
          <div style={{ marginTop: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <span style={{ fontWeight: 700, fontSize: '0.9rem', color: theme.text }}>Key Results</span>
              <button onClick={addKR} style={{ fontSize: '0.8rem', padding: '0.25rem 0.6rem', background: '#EDE9FE', color: '#7C3AED', border: 'none', borderRadius: '0.4rem', cursor: 'pointer', fontWeight: 600 }}>+ Add KR</button>
            </div>
            {keyResults.map((kr, i) => (
              <div key={i} style={{ background: '#F9FAFB', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '0.5rem', display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '0.5rem', alignItems: 'center' }}>
                <input style={inp} value={kr.title} onChange={e => updateKR(i, 'title', e.target.value)} placeholder="Key result title" />
                <input type="number" style={{ ...inp, width: 90 }} value={kr.target_value} onChange={e => updateKR(i, 'target_value', e.target.value)} placeholder="Target" />
                <input style={{ ...inp, width: 80 }} value={kr.unit} onChange={e => updateKR(i, 'unit', e.target.value)} placeholder="Unit" />
                <button onClick={() => removeKR(i)} style={{ background: '#FEE2E2', color: '#991B1B', border: 'none', borderRadius: '0.4rem', cursor: 'pointer', padding: '0.4rem 0.6rem', fontSize: '0.8rem' }}>✕</button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
          <button onClick={() => setShowCreate(false)} style={{ padding: '0.5rem 1.25rem', background: '#F3F4F6', color: theme.text, border: 'none', borderRadius: '0.5rem', cursor: 'pointer' }}>Cancel</button>
          <button onClick={saveGoal} disabled={saving} style={{ padding: '0.5rem 1.5rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving...' : editGoal ? 'Update Goal' : 'Create Goal'}
          </button>
        </div>
      </Modal>

      {/* Check-in Modal */}
      <Modal isOpen={!!checkinGoal} onClose={() => setCheckinGoal(null)} title={`Check-in: ${checkinGoal?.title}`} width={480}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text }}>Current Value {checkinGoal?.unit && `(${checkinGoal.unit})`}</label>
            <input type="number" style={inp} value={checkinForm.current_value} onChange={e => setCheckinForm(f => ({ ...f, current_value: e.target.value }))} placeholder={`Target: ${checkinGoal?.target_value ?? '—'}`} />
          </div>
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text }}>Update Status</label>
            <select style={{ ...inp, resize: undefined }} value={checkinForm.status} onChange={e => setCheckinForm(f => ({ ...f, status: e.target.value }))}>
              <option value="">Keep current</option>
              <option value="on_track">On Track</option>
              <option value="at_risk">At Risk</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text }}>Notes</label>
            <textarea style={{ ...inp, resize: 'vertical', minHeight: 80 }} value={checkinForm.notes} onChange={e => setCheckinForm(f => ({ ...f, notes: e.target.value }))} placeholder="Progress update, blockers, next steps..." />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button onClick={() => setCheckinGoal(null)} style={{ padding: '0.5rem 1.25rem', background: '#F3F4F6', color: theme.text, border: 'none', borderRadius: '0.5rem', cursor: 'pointer' }}>Cancel</button>
            <button onClick={saveCheckin} disabled={saving} style={{ padding: '0.5rem 1.5rem', background: '#3B82F6', color: '#fff', border: 'none', borderRadius: '0.5rem', fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving...' : 'Save Check-in'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─── Team Goals Tab ───────────────────────────────────────────────────────────

function TeamGoalsTab() {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    perfApi.getTeamGoals().then(r => { setGoals(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>Loading...</div>;

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: theme.textMuted, marginBottom: '1rem' }}>Goals from your direct reports</p>
      {goals.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted, background: theme.white, borderRadius: '0.75rem', border: `1px solid ${theme.border}` }}>No team goals found</div>
      ) : (
        goals.map(g => <GoalCard key={g.id} goal={g} isOwn={false} />)
      )}
    </div>
  );
}

// ─── Feedback & Journal Tab ───────────────────────────────────────────────────

function FeedbackTab({ user }) {
  const [tab, setTab] = useState('received');
  const [received, setReceived] = useState([]);
  const [journal, setJournal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ receiver_id: '', feedback_type: 'feedback', message: '', is_anonymous: false });
  const [saving, setSaving] = useState(false);

  const loadReceived = () => perfApi.getReceivedFeedback().then(r => setReceived(r.data || []));
  const loadJournal = () => perfApi.getMyJournal().then(r => setJournal(r.data || []));

  useEffect(() => {
    setLoading(true);
    Promise.all([loadReceived(), loadJournal()]).finally(() => setLoading(false));
  }, []);

  const saveFeedback = async () => {
    setSaving(true);
    try {
      await perfApi.giveFeedback({ ...form, receiver_id: form.receiver_id ? parseInt(form.receiver_id) : null });
      setShowForm(false);
      setForm({ receiver_id: '', feedback_type: 'feedback', message: '', is_anonymous: false });
      if (form.feedback_type === 'journal') loadJournal();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const FeedbackItem = ({ item }) => (
    <div style={{ background: theme.white, border: `1px solid ${theme.border}`, borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '0.6rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1.1rem' }}>{FEEDBACK_ICONS[item.feedback_type] || '💬'}</span>
          <span style={{ fontSize: '0.78rem', fontWeight: 700, padding: '0.15rem 0.5rem', background: '#F3F4F6', borderRadius: 999, color: theme.text, textTransform: 'capitalize' }}>{item.feedback_type}</span>
          {item.is_anonymous && <span style={{ fontSize: '0.72rem', color: theme.textMuted }}>Anonymous</span>}
        </div>
        <span style={{ fontSize: '0.72rem', color: theme.textMuted }}>{fmtDate(item.created_at)}</span>
      </div>
      <p style={{ margin: '0.6rem 0 0', fontSize: '0.875rem', color: theme.text, lineHeight: 1.6 }}>{item.message}</p>
      {item.giver_name && !item.is_anonymous && <p style={{ margin: '0.4rem 0 0', fontSize: '0.75rem', color: theme.textMuted }}>From: {item.giver_name}</p>}
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {[{ key: 'received', label: 'Received' }, { key: 'journal', label: 'My Journal' }].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{ padding: '0.4rem 1rem', borderRadius: '0.5rem', border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem', background: tab === t.key ? theme.primary : '#F3F4F6', color: tab === t.key ? '#fff' : theme.text }}>{t.label}</button>
          ))}
        </div>
        <button onClick={() => setShowForm(true)} style={{ padding: '0.4rem 1rem', background: '#10B981', color: '#fff', border: 'none', borderRadius: '0.5rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem' }}>
          + Give Feedback / Add Entry
        </button>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>Loading...</div> : (
        tab === 'received' ? (
          received.length === 0 ? <div style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>No feedback received yet</div> :
          received.map((item, i) => <FeedbackItem key={i} item={item} />)
        ) : (
          journal.length === 0 ? <div style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>No journal entries yet</div> :
          journal.map((item, i) => <FeedbackItem key={i} item={item} />)
        )
      )}

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title="Give Feedback / Add Journal Entry" width={500}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text }}>Type</label>
            <select style={{ ...inp, resize: undefined }} value={form.feedback_type} onChange={e => setForm(f => ({ ...f, feedback_type: e.target.value }))}>
              {FEEDBACK_TYPES.map(t => <option key={t} value={t}>{FEEDBACK_ICONS[t]} {t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </select>
          </div>
          {form.feedback_type !== 'journal' && (
            <div>
              <label style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text }}>Recipient Employee ID</label>
              <input type="number" style={inp} value={form.receiver_id} onChange={e => setForm(f => ({ ...f, receiver_id: e.target.value }))} placeholder="Employee ID" />
            </div>
          )}
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text }}>Message</label>
            <textarea style={{ ...inp, resize: 'vertical', minHeight: 100 }} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder={form.feedback_type === 'journal' ? 'Your journal entry...' : 'Your feedback...'} />
          </div>
          {form.feedback_type === 'feedback' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.is_anonymous} onChange={e => setForm(f => ({ ...f, is_anonymous: e.target.checked }))} />
              Send anonymously
            </label>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
            <button onClick={() => setShowForm(false)} style={{ padding: '0.5rem 1.25rem', background: '#F3F4F6', color: theme.text, border: 'none', borderRadius: '0.5rem', cursor: 'pointer' }}>Cancel</button>
            <button onClick={saveFeedback} disabled={saving || !form.message.trim()} style={{ padding: '0.5rem 1.5rem', background: '#10B981', color: '#fff', border: 'none', borderRadius: '0.5rem', fontWeight: 600, cursor: 'pointer', opacity: (saving || !form.message.trim()) ? 0.7 : 1 }}>
              {saving ? 'Saving...' : 'Submit'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─── Competencies Tab ─────────────────────────────────────────────────────────

function CompetenciesTab() {
  const [competencies, setCompetencies] = useState([]);
  const [myRatings, setMyRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rateModal, setRateModal] = useState(null); // competency being self-rated
  const [selfLevel, setSelfLevel] = useState('proficient');
  const [selfNotes, setSelfNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const currentYear = new Date().getFullYear();

  const load = () => {
    setLoading(true);
    Promise.all([
      perfApi.getCompetencies(),
      perfApi.getMyCompetencyRatings({ year: currentYear }),
    ]).then(([cRes, rRes]) => {
      setCompetencies(cRes.data || []);
      setMyRatings(rRes.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const ratingMap = {};
  myRatings.forEach(r => { ratingMap[r.competency_id] = r; });

  const openRate = (comp) => {
    const existing = ratingMap[comp.id];
    setSelfLevel(existing?.self_level || comp.expected_level || 'proficient');
    setSelfNotes(existing?.notes || '');
    setRateModal(comp);
  };

  const submitSelfRate = async () => {
    if (!rateModal) return;
    setSaving(true);
    try {
      await perfApi.selfRateCompetency({ competency_id: rateModal.id, self_level: selfLevel, cycle_year: currentYear, notes: selfNotes });
      setRateModal(null);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to save rating'); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>Loading...</div>;

  if (competencies.length === 0) {
    return <div style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted, background: theme.white, borderRadius: '0.75rem', border: `1px solid ${theme.border}` }}>No competencies defined yet. Ask your HR admin to set up the competency framework.</div>;
  }

  // Group by department > category
  const grouped = {};
  competencies.forEach(comp => {
    const dept = comp.department || 'General';
    const cat = comp.category || 'Uncategorized';
    const key = `${dept}|||${cat}`;
    if (!grouped[key]) grouped[key] = { dept, cat, items: [] };
    grouped[key].items.push(comp);
  });

  return (
    <div>
      {Object.values(grouped).map(({ dept, cat, items }) => (
        <div key={`${dept}-${cat}`} style={{ marginBottom: '1.5rem' }}>
          {/* Department > Category header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: 999, background: '#EDE9FE', color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{dept}</span>
            <span style={{ color: theme.textMuted, fontSize: '0.8rem' }}>›</span>
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: theme.text }}>{cat}</span>
            <span style={{ fontSize: '0.72rem', color: theme.textMuted }}>({items.length})</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '0.75rem' }}>
            {items.map(comp => {
              const rating = ratingMap[comp.id];
              const selfLvl = rating?.self_level;
              const managerLvl = rating?.manager_level;
              const expLvl = comp.expected_level || 'proficient';
              const selfIdx = COMPETENCY_LEVELS.indexOf(selfLvl);
              const expIdx = COMPETENCY_LEVELS.indexOf(expLvl);
              const meetsExpected = selfIdx >= expIdx;

              return (
                <div key={comp.id} style={{ background: theme.white, border: `1px solid ${theme.border}`, borderRadius: '0.75rem', padding: '1.1rem 1.25rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.4rem' }}>
                    <p style={{ margin: 0, fontWeight: 700, color: theme.text, fontSize: '0.9rem', flex: 1 }}>{comp.name}</p>
                    <button
                      onClick={() => openRate(comp)}
                      style={{ padding: '0.2rem 0.6rem', fontSize: '0.72rem', background: selfLvl ? '#D1FAE5' : '#DBEAFE', color: selfLvl ? '#065F46' : '#1D4ED8', border: 'none', borderRadius: '0.4rem', cursor: 'pointer', fontWeight: 600, flexShrink: 0, marginLeft: '0.5rem' }}
                    >
                      {selfLvl ? 'Update' : 'Rate Self'}
                    </button>
                  </div>
                  {comp.description && <p style={{ margin: '0 0 0.6rem', fontSize: '0.78rem', color: theme.textMuted }}>{comp.description}</p>}

                  {/* Self-rating bar */}
                  <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.5rem' }}>
                    {COMPETENCY_LEVELS.map((lvl, i) => (
                      <div key={lvl} title={lvl} style={{ flex: 1, height: 7, borderRadius: 3, background: selfIdx >= i ? COMPETENCY_COLORS[lvl] : '#E5E7EB', transition: 'background 0.2s' }} />
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.75rem', flexWrap: 'wrap' }}>
                    <div>
                      <span style={{ color: theme.textMuted }}>Self: </span>
                      <span style={{ fontWeight: 700, color: selfLvl ? COMPETENCY_COLORS[selfLvl] : '#9CA3AF', textTransform: 'capitalize' }}>{selfLvl || 'Not rated'}</span>
                    </div>
                    {managerLvl && (
                      <div>
                        <span style={{ color: theme.textMuted }}>Manager: </span>
                        <span style={{ fontWeight: 700, color: COMPETENCY_COLORS[managerLvl], textTransform: 'capitalize' }}>{managerLvl}</span>
                      </div>
                    )}
                    <div>
                      <span style={{ color: theme.textMuted }}>Expected: </span>
                      <span style={{ fontWeight: 700, color: meetsExpected ? '#10B981' : '#F59E0B', textTransform: 'capitalize' }}>{expLvl}</span>
                      {selfLvl && <span style={{ marginLeft: '0.25rem' }}>{meetsExpected ? '✓' : '↑'}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Self-rate Modal */}
      <Modal isOpen={!!rateModal} onClose={() => setRateModal(null)} title={`Rate Yourself: ${rateModal?.name}`} width={440}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text, display: 'block', marginBottom: '0.5rem' }}>Select Your Proficiency Level</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {COMPETENCY_LEVELS.map(lvl => (
                <button key={lvl} onClick={() => setSelfLevel(lvl)} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 1rem', border: `2px solid ${selfLevel === lvl ? COMPETENCY_COLORS[lvl] : '#E5E7EB'}`, borderRadius: '0.5rem', background: selfLevel === lvl ? `${COMPETENCY_COLORS[lvl]}18` : '#fff', cursor: 'pointer', textAlign: 'left' }}>
                  <div style={{ width: 12, height: 12, borderRadius: '50%', background: COMPETENCY_COLORS[lvl], flexShrink: 0 }} />
                  <span style={{ fontWeight: selfLevel === lvl ? 700 : 500, color: selfLevel === lvl ? COMPETENCY_COLORS[lvl] : theme.text, textTransform: 'capitalize', fontSize: '0.875rem' }}>{lvl}</span>
                  {rateModal?.expected_level === lvl && <span style={{ fontSize: '0.7rem', color: '#6B7280', marginLeft: 'auto' }}>Expected</span>}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text, display: 'block', marginBottom: '0.3rem' }}>Notes (optional)</label>
            <textarea style={{ ...inp, resize: 'vertical', minHeight: 65 }} value={selfNotes} onChange={e => setSelfNotes(e.target.value)} placeholder="Evidence, examples, or context for your rating..." />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button onClick={() => setRateModal(null)} style={{ padding: '0.5rem 1.25rem', background: '#F3F4F6', color: theme.text, border: 'none', borderRadius: '0.5rem', cursor: 'pointer' }}>Cancel</button>
            <button onClick={submitSelfRate} disabled={saving} style={{ padding: '0.5rem 1.5rem', background: COMPETENCY_COLORS[selfLevel] || theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving...' : 'Save Rating'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─── Team's Assessment Tab (Managers) ────────────────────────────────────────

function TeamAssessmentsTab() {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewModal, setReviewModal] = useState(null);
  const [mgr, setMgr] = useState({}); // { [field_id]: { manager_rating, manager_comment } }
  const [overallComment, setOverallComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [viewModal, setViewModal] = useState(null);

  const load = () => {
    setLoading(true);
    perfApi.getTeamAssessments().then(r => { setAssessments(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openReview = (a) => {
    // Pre-fill with existing manager ratings if any
    const initial = {};
    (a.responses || []).forEach(r => {
      initial[r.field_id] = { manager_rating: r.manager_rating || null, manager_comment: r.manager_comment || '' };
    });
    // Also add blank entries for fields that have no response yet
    (a.form_fields || []).forEach(f => {
      if (!initial[f.id]) initial[f.id] = { manager_rating: null, manager_comment: '' };
    });
    setMgr(initial);
    setOverallComment(a.overall_manager_comment || '');
    setReviewModal(a);
  };

  const submitReview = async () => {
    if (!reviewModal) return;
    setSubmitting(true);
    try {
      const responses = Object.entries(mgr)
        .filter(([, v]) => v.manager_rating || v.manager_comment)
        .map(([field_id, v]) => ({ field_id: parseInt(field_id), manager_rating: v.manager_rating, manager_comment: v.manager_comment || null }));
      await perfApi.submitReview(reviewModal.id, { responses, overall_manager_comment: overallComment });
      setReviewModal(null);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to submit review'); }
    finally { setSubmitting(false); }
  };

  const scoreColor = (s) => {
    if (!s) return theme.textMuted;
    if (s >= 8) return '#10B981'; if (s >= 6) return '#3B82F6';
    if (s >= 4) return '#F59E0B'; return '#EF4444';
  };

  // Group by status
  const pending = assessments.filter(a => a.status === 'submitted');
  const reviewed = assessments.filter(a => a.status === 'manager_reviewed');

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>Loading...</div>;

  const AssessmentRow = ({ a, showReviewBtn }) => (
    <div style={{ background: theme.white, border: `1px solid ${theme.border}`, borderRadius: '0.75rem', padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
      <div style={{ flex: 1 }}>
        <p style={{ margin: 0, fontWeight: 700, color: theme.text }}>{a.employee_name}</p>
        <p style={{ margin: '0.15rem 0 0', fontSize: '0.78rem', color: theme.textMuted }}>
          {a.designation || a.department || '—'} • {a.cycle_name} • {a.form_name}
        </p>
        {a.employee_submitted_at && <p style={{ margin: '0.1rem 0 0', fontSize: '0.72rem', color: theme.textMuted }}>Submitted: {fmtDate(a.employee_submitted_at)}</p>}
      </div>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        {a.overall_employee_score && <div style={{ fontSize: '0.85rem' }}>Emp: <strong style={{ color: scoreColor(a.overall_employee_score) }}>{a.overall_employee_score}/10</strong></div>}
        {a.overall_manager_score && <div style={{ fontSize: '0.85rem' }}>Mgr: <strong style={{ color: scoreColor(a.overall_manager_score) }}>{a.overall_manager_score}/10</strong></div>}
        <button onClick={() => setViewModal(a)} style={{ padding: '0.3rem 0.75rem', fontSize: '0.78rem', background: '#F3F4F6', color: theme.text, border: 'none', borderRadius: '0.4rem', cursor: 'pointer' }}>View</button>
        {showReviewBtn && (
          <button onClick={() => openReview(a)} style={{ padding: '0.3rem 0.75rem', fontSize: '0.78rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.4rem', cursor: 'pointer', fontWeight: 600 }}>
            Fill Review
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div>
      {assessments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted, background: theme.white, borderRadius: '0.75rem', border: `1px solid ${theme.border}` }}>
          No submitted assessments from your team yet
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.text, margin: '0 0 0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                Pending Review <span style={{ background: '#FEF3C7', color: '#92400E', borderRadius: 999, padding: '0.1rem 0.5rem', fontSize: '0.72rem' }}>{pending.length}</span>
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {pending.map(a => <AssessmentRow key={a.id} a={a} showReviewBtn />)}
              </div>
            </div>
          )}
          {reviewed.length > 0 && (
            <div>
              <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.text, margin: '0 0 0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                Reviewed <span style={{ background: '#D1FAE5', color: '#065F46', borderRadius: 999, padding: '0.1rem 0.5rem', fontSize: '0.72rem' }}>{reviewed.length}</span>
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {reviewed.map(a => <AssessmentRow key={a.id} a={a} showReviewBtn={false} />)}
              </div>
            </div>
          )}
        </>
      )}

      {/* Review Modal */}
      <Modal isOpen={!!reviewModal} onClose={() => setReviewModal(null)} title={`Review: ${reviewModal?.employee_name}`} width={700}>
        {reviewModal && (
          <div>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.25rem', background: '#F9FAFB', borderRadius: '0.5rem', padding: '0.75rem 1rem' }}>
              <div><span style={{ fontSize: '0.72rem', color: theme.textMuted }}>Cycle</span><p style={{ margin: '0.1rem 0 0', fontWeight: 600, fontSize: '0.85rem' }}>{reviewModal.cycle_name}</p></div>
              <div><span style={{ fontSize: '0.72rem', color: theme.textMuted }}>Form</span><p style={{ margin: '0.1rem 0 0', fontWeight: 600, fontSize: '0.85rem' }}>{reviewModal.form_name}</p></div>
              <div><span style={{ fontSize: '0.72rem', color: theme.textMuted }}>Emp Score</span><p style={{ margin: '0.1rem 0 0', fontWeight: 700, fontSize: '0.9rem', color: scoreColor(reviewModal.overall_employee_score) }}>{reviewModal.overall_employee_score ? `${reviewModal.overall_employee_score}/10` : '—'}</p></div>
            </div>

            {/* Group fields by section */}
            {(() => {
              const grouped = {};
              const allFields = reviewModal.form_fields || [];
              allFields.forEach(f => {
                const sec = f.section || 'General';
                if (!grouped[sec]) grouped[sec] = [];
                grouped[sec].push(f);
              });
              const respMap = {};
              (reviewModal.responses || []).forEach(r => { respMap[r.field_id] = r; });

              return Object.entries(grouped).map(([section, fields]) => (
                <div key={section} style={{ marginBottom: '1.25rem' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: theme.primary, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem', paddingBottom: '0.3rem', borderBottom: `1px solid ${theme.border}` }}>{section}</div>
                  {fields.map(f => {
                    const resp = respMap[f.id];
                    const cur = mgr[f.id] || {};
                    return (
                      <div key={f.id} style={{ background: '#F9FAFB', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '0.5rem' }}>
                        <p style={{ margin: '0 0 0.4rem', fontWeight: 600, fontSize: '0.85rem', color: theme.text }}>{f.label}</p>
                        {f.description && <p style={{ margin: '0 0 0.5rem', fontSize: '0.75rem', color: theme.textMuted }}>{f.description}</p>}
                        {/* Employee response */}
                        {resp && (
                          <div style={{ marginBottom: '0.5rem', fontSize: '0.78rem' }}>
                            <span style={{ color: theme.textMuted }}>Employee: </span>
                            {f.field_type === 'rating'
                              ? <strong style={{ color: scoreColor(resp.employee_rating) }}>{resp.employee_rating ? `${resp.employee_rating}/10 — ${RATING_LABELS[resp.employee_rating]}` : '—'}</strong>
                              : <span style={{ color: theme.text }}>{resp.employee_comment || '—'}</span>
                            }
                            {resp.employee_comment && f.field_type === 'rating' && <span style={{ color: theme.textMuted, fontStyle: 'italic' }}> · "{resp.employee_comment}"</span>}
                          </div>
                        )}
                        {/* Manager input */}
                        {f.field_type === 'rating' ? (
                          <div>
                            <div style={{ fontSize: '0.75rem', color: theme.textMuted, marginBottom: '0.3rem' }}>Your Rating:</div>
                            <RatingInput
                              value={cur.manager_rating}
                              onChange={v => setMgr(prev => ({ ...prev, [f.id]: { ...prev[f.id], manager_rating: v } }))}
                            />
                          </div>
                        ) : (
                          <div>
                            <div style={{ fontSize: '0.75rem', color: theme.textMuted, marginBottom: '0.3rem' }}>Your Comment:</div>
                            <textarea style={{ ...inp, resize: 'vertical', minHeight: 55 }} value={cur.manager_comment || ''} onChange={e => setMgr(prev => ({ ...prev, [f.id]: { ...prev[f.id], manager_comment: e.target.value } }))} placeholder="Manager comment..." />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ));
            })()}

            {/* Overall comment */}
            <div style={{ marginTop: '0.5rem' }}>
              <label style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text, display: 'block', marginBottom: '0.3rem' }}>Overall Manager Comment</label>
              <textarea style={{ ...inp, resize: 'vertical', minHeight: 80 }} value={overallComment} onChange={e => setOverallComment(e.target.value)} placeholder="Overall assessment, strengths, areas of improvement..." />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.25rem' }}>
              <button onClick={() => setReviewModal(null)} style={{ padding: '0.5rem 1.25rem', background: '#F3F4F6', color: theme.text, border: 'none', borderRadius: '0.5rem', cursor: 'pointer' }}>Cancel</button>
              <button onClick={submitReview} disabled={submitting} style={{ padding: '0.5rem 1.5rem', background: '#10B981', color: '#fff', border: 'none', borderRadius: '0.5rem', fontWeight: 600, cursor: 'pointer', opacity: submitting ? 0.7 : 1 }}>
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* View Modal (read-only) */}
      <Modal isOpen={!!viewModal} onClose={() => setViewModal(null)} title={`Assessment: ${viewModal?.employee_name}`} width={600}>
        {viewModal && (
          <div style={{ fontSize: '0.875rem' }}>
            {(() => {
              const grouped = {};
              (viewModal.responses || []).forEach(r => {
                const sec = r.field_section || 'General';
                if (!grouped[sec]) grouped[sec] = [];
                grouped[sec].push(r);
              });
              return Object.entries(grouped).map(([section, responses]) => (
                <div key={section} style={{ marginBottom: '1rem' }}>
                  <p style={{ margin: '0 0 0.5rem', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: theme.primary }}>{section}</p>
                  {responses.map((r, i) => (
                    <div key={i} style={{ padding: '0.6rem 0.75rem', background: '#F9FAFB', borderRadius: '0.5rem', marginBottom: '0.35rem' }}>
                      <p style={{ margin: '0 0 0.3rem', fontWeight: 600, color: theme.text, fontSize: '0.85rem' }}>{r.field_label}</p>
                      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.78rem' }}>
                        {r.field_type === 'rating' ? (
                          <>
                            <div><span style={{ color: theme.textMuted }}>Self: </span><strong style={{ color: scoreColor(r.employee_rating) }}>{r.employee_rating ? `${r.employee_rating}/10` : '—'}</strong></div>
                            <div><span style={{ color: theme.textMuted }}>Manager: </span><strong style={{ color: scoreColor(r.manager_rating) }}>{r.manager_rating ? `${r.manager_rating}/10` : '—'}</strong></div>
                          </>
                        ) : <span style={{ color: theme.text }}>{r.employee_comment || '—'}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ));
            })()}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button onClick={() => setViewModal(null)} style={{ padding: '0.5rem 1.25rem', background: '#F3F4F6', color: theme.text, border: 'none', borderRadius: '0.5rem', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ─── Team Analytics Tab ───────────────────────────────────────────────────────

function TeamAnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    perfApi.getTeamAnalytics().then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>Loading...</div>;
  if (!data) return <div style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>No analytics data available</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Summary stats */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <StatPill label="Team Size" value={data.team_size ?? 0} color="#3B82F6" />
        <StatPill label="Total Goals" value={data.total_goals ?? 0} color="#8B5CF6" />
        <StatPill label="Completed" value={data.completed_goals ?? 0} color="#10B981" />
        <StatPill label="At Risk" value={data.at_risk_goals ?? 0} color="#F59E0B" />
        <StatPill label="Avg Progress" value={`${data.avg_progress ?? 0}%`} color="#0EA5E9" />
      </div>

      {/* Member breakdown */}
      {data.members?.length > 0 && (
        <div style={{ background: theme.white, border: `1px solid ${theme.border}`, borderRadius: '0.75rem', padding: '1.25rem' }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: 700, color: theme.text }}>Team Member Performance</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {data.members.map((m, i) => {
              const pct = m.avg_progress ?? 0;
              const color = pct >= 80 ? '#10B981' : pct >= 50 ? '#3B82F6' : '#F59E0B';
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ width: 140, fontSize: '0.85rem', fontWeight: 600, color: theme.text, flexShrink: 0 }}>{m.name}</div>
                  <div style={{ flex: 1 }}>
                    <ProgressBar value={pct} color={color} />
                  </div>
                  <div style={{ width: 50, textAlign: 'right', fontSize: '0.85rem', fontWeight: 700, color, flexShrink: 0 }}>{pct}%</div>
                  <div style={{ width: 80, fontSize: '0.75rem', color: theme.textMuted, flexShrink: 0 }}>{m.goal_count ?? 0} goals</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Assessments Tab (legacy/existing reviews) ────────────────────────────────

function AssessmentsTab({ user }) {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    perfApi.getMyAssessments().then(r => { setAssessments(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const statusColor = (s) => {
    if (s === 'completed') return '#10B981';
    if (s === 'in_progress') return '#F59E0B';
    if (s === 'pending') return '#6B7280';
    return theme.textMuted;
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>Loading...</div>;

  return (
    <div>
      {assessments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted, background: theme.white, borderRadius: '0.75rem', border: `1px solid ${theme.border}` }}>
          No assessments yet
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {assessments.map((a) => (
            <div key={a.id} style={{ background: theme.white, border: `1px solid ${theme.border}`, borderRadius: '0.75rem', padding: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <div>
                  <p style={{ margin: 0, fontWeight: 700, color: theme.text }}>{a.title || `Assessment #${a.id}`}</p>
                  <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>{fmtDate(a.created_at)}</p>
                </div>
                <span style={{ fontSize: '0.78rem', fontWeight: 700, padding: '0.25rem 0.75rem', borderRadius: 999, background: `${statusColor(a.status)}22`, color: statusColor(a.status), textTransform: 'capitalize' }}>
                  {a.status?.replace('_', ' ')}
                </span>
              </div>
              {a.self_rating && (
                <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.85rem', flexWrap: 'wrap' }}>
                  <div>Self Rating: <strong style={{ color: ratingColor(a.self_rating) }}>{a.self_rating}/10</strong></div>
                  {a.manager_rating && <div>Manager Rating: <strong style={{ color: ratingColor(a.manager_rating) }}>{a.manager_rating}/10</strong></div>}
                  {a.final_rating && <div>Final: <strong style={{ color: ratingColor(a.final_rating) }}>{a.final_rating}/10</strong></div>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function MyPerformance() {
  const { user } = useAuth();
  // Role-based manager: hr_admin / super_admin / reporting_manager
  const isRoleManager = ['reporting_manager', 'hr_admin', 'super_admin'].includes(user?.role);
  // Team-lead detection: any employee with direct reports qualifies, regardless of role.
  // We call the team-assessments API once — if it succeeds (200), this user is a team lead.
  const [isTeamLead, setIsTeamLead] = useState(isRoleManager);
  const [teamLeadChecked, setTeamLeadChecked] = useState(isRoleManager);

  useEffect(() => {
    if (isRoleManager) return; // already known
    perfApi.getTeamAssessments()
      .then(() => { setIsTeamLead(true); setTeamLeadChecked(true); })
      .catch(() => { setIsTeamLead(false); setTeamLeadChecked(true); });
  }, [isRoleManager]);

  const showManagerTabs = isTeamLead;

  const tabs = [
    { key: 'assessments', label: 'My Assessments' },
    { key: 'goals', label: 'My Goals' },
    ...(showManagerTabs ? [
      { key: 'team_assessments', label: "Team's Assessment" },
      { key: 'team_goals', label: 'Team Goals' },
      { key: 'team_analytics', label: 'Team Analytics' },
    ] : []),
    { key: 'feedback', label: 'Feedback & Journal' },
    { key: 'competencies', label: 'Competencies' },
  ];

  const [activeTab, setActiveTab] = useState('assessments');

  // Don't render tabs until we know if the user is a team lead
  if (!teamLeadChecked) return <div style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted }}>Loading...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Header */}
      <div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>My Performance</h1>
        <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>Track goals, give feedback, and monitor your growth</p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: `2px solid ${theme.border}`, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: '0.6rem 1.1rem', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 600,
            fontSize: '0.875rem', color: activeTab === t.key ? theme.primary : theme.textMuted,
            borderBottom: `2px solid ${activeTab === t.key ? theme.primary : 'transparent'}`,
            marginBottom: -2, transition: 'color 0.15s',
          }}>{t.label}</button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'assessments' && <AssessmentsTab user={user} />}
        {activeTab === 'goals' && <MyGoalsTab user={user} />}
        {activeTab === 'team_assessments' && showManagerTabs && <TeamAssessmentsTab />}
        {activeTab === 'team_goals' && showManagerTabs && <TeamGoalsTab />}
        {activeTab === 'team_analytics' && showManagerTabs && <TeamAnalyticsTab />}
        {activeTab === 'feedback' && <FeedbackTab user={user} />}
        {activeTab === 'competencies' && <CompetenciesTab />}
      </div>
    </div>
  );
}
